import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Toast from '../components/Toast';
import './EmployeeRegister.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function EmployeeRegister() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
  });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  useEffect(() => {
    if (!token) {
      showToast('No invite token found in the URL.', 'error');
      setLoading(false);
      return;
    }

    fetch(`${API_URL}/api/invites/validate?token=${token}`)
      .then((res) => {
        if (!res.ok) throw new Error('Invalid invite');
        return res.json();
      })
      .then((data) => {
        setInviteData(data);
        setLoading(false);
      })
      .catch((err) => {
        showToast('Invalid or expired invitation link.', 'error');
        setLoading(false);
      });
  }, [token]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${API_URL}/api/employees/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          firstName: formData.firstName,
          lastName: formData.lastName,
          password: formData.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Registration failed');
      }

      showToast('Account created successfully. Redirecting to login...', 'success');
      
      // Redirect after showing success toast
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      showToast(err.message || 'Registration failed.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="employee-register-container">
        <div className="employee-register-card">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!inviteData && !loading) {
    return (
      <div className="employee-register-container">
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
        <div className="employee-register-card">
          <div className="error-icon">⚠️</div>
          <h2>Unable to Process Invitation</h2>
          <p className="error-message">
            The invitation link is invalid or has expired.
          </p>
          <button onClick={() => navigate('/login')} className="btn-secondary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-register-container">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      <div className="employee-register-card">
        <div className="card-header">
          <h2>Join {inviteData.companyName || 'Your Company'}</h2>
          <p className="subtitle">Complete your account setup</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              id="email"
              type="email"
              value={inviteData.email}
              disabled
              className="form-input disabled"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="form-input"
                placeholder="John"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="form-input"
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter a secure password"
              minLength="8"
            />
            <small className="form-hint">Minimum 8 characters</small>
          </div>

          <button type="submit" className="btn-primary">
            Create Account
          </button>
        </form>
      </div>
    </div>
  );
}

